/**
 * Removes all whitespace from a string (for IBAN, phone, NIR, etc.).
 * @param {string} value
 * @returns {string}
 */
export const stripAllSpaces = (value) => {
  if (value == null) return value;
  return String(value).replace(/\s/g, "");
};

/**
 * Collapses consecutive whitespace to a single space (Type B — live input).
 * @param {string} value
 * @returns {string}
 */
export const filterTextField = (value) => {
  if (value == null) return value;
  return String(value).replace(/\s{2,}/g, " ");
};

/**
 * Trims edges and collapses consecutive whitespace (Type B — blur / validation).
 * @param {string} value
 * @returns {string}
 */
export const normalizeTextField = (value) => {
  if (value == null) return value;
  return filterTextField(value).trim();
};

/**
 * @param {string} value
 * @returns {boolean}
 */
export const isValidEmailAddress = (value) => {
  if (typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 254) {
    return false;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

/**
 * Filters and cleans city name input according to validation rules
 * @param {string} value - The input value to filter
 * @returns {string} - The filtered and cleaned value
 */
export const filterCityName = (value) => {
  if (!value) return "";

  // Filter invalid characters (keep only letters, spaces, hyphens, apostrophes)
  let filteredValue = value.replace(/[^A-Za-zÀ-ÿ\s'-]/g, "");

  // Prevent multiple consecutive spaces
  filteredValue = filteredValue.replace(/\s{2,}/g, " ");

  // Prevent multiple consecutive hyphens
  filteredValue = filteredValue.replace(/-{2,}/g, "-");

  // Prevent multiple consecutive apostrophes
  filteredValue = filteredValue.replace(/'{2,}/g, "'");

  // Prevent apostrophe-hyphen combinations
  filteredValue = filteredValue.replace(/'-/g, "'");
  filteredValue = filteredValue.replace(/-'/g, "-");

  // Remove leading apostrophes or hyphens
  filteredValue = filteredValue.replace(/^['-]+/, "");

  // Limit to 50 characters
  filteredValue = filteredValue.slice(0, 50);

  return filteredValue;
};

/**
 * Creates a city name validation schema for Yup
 * @param {object} messages - Validation messages object
 * @param {object} REGEX_EXPS - Regular expressions object
 * @returns {object} - Yup validation schema
 */
export const createCityNameValidation = (messages, REGEX_EXPS) => {
  const yup = require("yup");

  return yup
    .string()
    .typeError(messages.REQUIRED)
    .required(messages.REQUIRED)
    .min(2, messages.INVALID_CITY_NAME)
    .max(50, messages.INVALID_CITY_NAME)
    .transform(normalizeTextField)
    .matches(REGEX_EXPS.cityName, messages.INVALID_CITY_NAME)
    .test(
      "no-multiple-spaces",
      messages.INVALID_CITY_NAME,
      (value) => !value || !/\s{2,}/.test(value),
    )
    .test(
      "no-multiple-hyphens",
      messages.INVALID_CITY_NAME,
      (value) => !value || !/-{2,}/.test(value),
    )
    .test(
      "no-multiple-apostrophes",
      messages.INVALID_CITY_NAME,
      (value) => !value || !/'{2,}/.test(value),
    )
    .test(
      "no-apostrophe-hyphen-combo",
      messages.INVALID_CITY_NAME,
      (value) => !value || (!/'[-]/.test(value) && !/-[']/.test(value)),
    )
    .test(
      "no-leading-special-chars",
      messages.INVALID_CITY_NAME,
      (value) => !value || !/^['-]/.test(value),
    )
    .test(
      "no-trailing-special-chars",
      messages.INVALID_CITY_NAME,
      (value) => !value || !/['-]$/.test(value),
    );
};

/**
 * Filters and cleans contract number input according to validation rules
 * @param {string} value - The input value to filter
 * @returns {string} - The filtered and cleaned value
 */
export const filterContractNumber = (value) => {
  if (!value) return "";

  // Keep only letters, numbers, hyphens (no spaces)
  let filteredValue = value.replace(/[^A-Za-z0-9-]/g, "");

  // Prevent multiple consecutive hyphens
  filteredValue = filteredValue.replace(/-{2,}/g, "-");

  // Remove leading and trailing hyphens
  filteredValue = filteredValue.replace(/^-+/, "");
  filteredValue = filteredValue.replace(/-+$/, "");

  return filteredValue;
};

const withContractNumberFormatTests = (schema, messages, REGEX_EXPS) =>
  schema
    .transform(stripAllSpaces)
    .test(
      "contract-number-format",
      messages.INVALID_CONTRACT_NUMBER,
      (value) => !value || REGEX_EXPS.contractNumber.test(value),
    )
    .test(
      "no-multiple-hyphens",
      messages.INVALID_CONTRACT_NUMBER,
      (value) => !value || !/-{2,}/.test(value),
    )
    .test(
      "no-leading-hyphens",
      messages.INVALID_CONTRACT_NUMBER,
      (value) => !value || !/^-+/.test(value),
    )
    .test(
      "no-trailing-hyphens",
      messages.INVALID_CONTRACT_NUMBER,
      (value) => !value || !/-+$/.test(value),
    );

/**
 * Creates a contract number validation schema for Yup
 * @param {object} messages - Validation messages object
 * @param {object} REGEX_EXPS - Regular expressions object
 * @param {{ required?: boolean }} [options]
 * @returns {object} - Yup validation schema
 */
export const createContractNumberValidation = (
  messages,
  REGEX_EXPS,
  { required = true } = {},
) => {
  const yup = require("yup");

  let schema = withContractNumberFormatTests(
    yup.string().nullable(),
    messages,
    REGEX_EXPS,
  );

  if (required) {
    return schema.typeError(messages.REQUIRED).required(messages.REQUIRED);
  }

  return schema.notRequired();
};

export const isValidIpRule = (value) => {
  if (typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  const [address, bits, ...extra] = trimmed.split("/");
  if (extra.length) {
    return false;
  }
  const octets = address.split(".");
  if (octets.length !== 4) {
    return false;
  }
  const octetsValid = octets.every(
    (octet) =>
      /^\d{1,3}$/.test(octet) && Number(octet) >= 0 && Number(octet) <= 255,
  );
  if (!octetsValid) {
    return false;
  }
  if (bits === undefined) {
    return true;
  }
  return /^\d{1,2}$/.test(bits) && Number(bits) >= 0 && Number(bits) <= 32;
};
